import posthog from 'posthog-js'

import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('TransactionTracking')

/**
 * Track a user action with additional properties
 * @param eventName The name of the event
 * @param properties Additional properties to track
 */
export function trackEvent(eventName: string, properties: Record<string, any> = {}): void {
    try {
        // Track the event directly with PostHog
        posthog.capture(eventName, properties)
        logger.debug(`Tracked event: ${eventName}`, properties)
    } catch (error) {
        logger.error(`Error tracking event: ${eventName}`, error)
    }
}

/**
 * Track a transaction completion directly with PostHog
 * This is a simplified version that doesn't depend on the application store
 * to avoid circular dependencies
 */
export function trackTransactionCompletion(
    walletAddress: string,
    chainType: string,
    transactionValueUSD?: number
): void {
    try {
        if (!transactionValueUSD || transactionValueUSD <= 0) return

        // Track the basic transaction completion event
        posthog.capture('transaction_completed', {
            wallet_address: walletAddress,
            chain_type: chainType,
            transaction_value_usd: transactionValueUSD,
            timestamp: new Date().toISOString(),
        })

        // Get current contribution values from PostHog
        const currentProperties = posthog.get_property('contribution_total_usd') || 0
        const transactionCount = posthog.get_property('contribution_transaction_count') || 0

        // Calculate new values
        const newTotalContribution = Number(currentProperties) + transactionValueUSD
        const newTransactionCount = Number(transactionCount) + 1
        const averageContribution = newTransactionCount > 0 ? newTotalContribution / newTransactionCount : 0

        // Update user properties with contribution metrics
        posthog.people.set({
            contribution_total_usd: newTotalContribution,
            contribution_transaction_count: newTransactionCount,
            contribution_average_usd: averageContribution,
            last_contribution_value_usd: transactionValueUSD,
            last_contribution_date: new Date().toISOString(),
        })

        // Track contribution increment
        posthog.capture('contribution_increment', {
            contribution_total_usd: transactionValueUSD,
            contribution_transaction_count: 1,
        })

        // Track user contribution update
        posthog.capture('user_contribution_updated', {
            wallet_address: walletAddress,
            transaction_value_usd: transactionValueUSD,
            total_contribution_usd: newTotalContribution,
            transaction_count: newTransactionCount,
            average_contribution_usd: averageContribution,
            timestamp: new Date().toISOString(),
        })

        logger.debug('Tracked transaction completion:', {
            walletAddress,
            chainType,
            transactionValueUSD,
        })
    } catch (error) {
        logger.error('Error tracking transaction completion:', error)
    }
}
